import React, { useRef, useState, useImperativeHandle, useEffect } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';

import { useInView } from 'react-intersection-observer';

const selectMenuFactory = MenuComponent => {
  const SelectMenu = ({
    autoMenuPlacement,
    intersectionRoot,
    intersectionThreshold,
    onMenuPlacementChange,
    placement,
    ...otherProps
  }) => {
    // Refs
    const ref = useRef(null);

    // State
    const [menuPlacement, setMenuPlacement] = useState(placement);

    // Effects
    const [menuRef, isIntersecting, entry] = useInView({
      threshold: intersectionThreshold,
      root: intersectionRoot,
    });

    useEffect(() => {
      if (autoMenuPlacement && entry && menuPlacement === 'bottom') {
        // A 'bottom' menu can flip to 'top' but not vice-versa, to prevent endless loops
        const { rootBounds, target } = entry;

        // Due to the asynchronous nature of the IntersectionObserver API, the boundingClientRect
        // returned on entry can sometimes be inaccurate depending on the time it is measured vs
        // final DOM layout. Instead we measure the bounding client here so that the size is consistent.
        const boundingClientRect = target.getBoundingClientRect();

        if (
          boundingClientRect.top - boundingClientRect.height >=
            rootBounds.top && // It fits at the top
          boundingClientRect.bottom >= rootBounds.bottom
        ) {
          // It doesn't fit at the bottom
          setMenuPlacement('top');
        }
      }
    }, [autoMenuPlacement, isIntersecting, entry]);

    useEffect(() => {
      if (autoMenuPlacement) {
        onMenuPlacementChange(menuPlacement);
      }
    }, [autoMenuPlacement, menuPlacement, onMenuPlacementChange]);

    useImperativeHandle(ref, () => menuRef.current);

    return (
      <div ref={menuRef}>
        <MenuComponent {...otherProps} />
      </div>
    );
  };

  if (MenuComponent.name) {
    SelectMenu.displayName = `Charts${MenuComponent.name}`;
  }

  SelectMenu.propTypes = {
    ...MenuComponent.propTypes,
    intersectionRoot: PropTypes.any,
    intersectionThreshold: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.number),
    ]),
    onMenuPlacementChange: PropTypes.func,
    placement: PropTypes.oneOf(['top', 'bottom']),
  };

  SelectMenu.defaultProps = {
    ...MenuComponent.defaultProps,
    intersectionRoot: null,
    intersectionThreshold: 0,
    onMenuPlacementChange: noop,
    placement: 'bottom',
  };

  return SelectMenu;
};

export default selectMenuFactory;
export { selectMenuFactory };
